import { O_BOUNDS, O_GLYPH_PATH, getRenderModel } from "./svg-source";
import type { BuilderConfig } from "./types";

interface Props {
  config: BuilderConfig;
  /** Visualize the clear space: dashed bounds + ghost "o" glyphs in the gutter. */
  showGuides?: boolean;
  className?: string;
}

export function LogoSvg({ config, showGuides = false, className }: Props) {
  const { geometry, background, paths } = getRenderModel(config);
  const { viewBox, content, o } = geometry;

  const guideColor = config.background === "white" ? "#0A0A0B" : "#FFFFFF";
  // Ghost "o" scaled so its height fills one clear-space unit, centered in the corner cell.
  const ghostScale = o / O_BOUNDS.h;
  const ghostInset = (o - O_BOUNDS.w * ghostScale) / 2;
  const corners = [
    { x: content.x - o, y: content.y - o },
    { x: content.x + content.w, y: content.y - o },
    { x: content.x - o, y: content.y + content.h },
    { x: content.x + content.w, y: content.y + content.h },
  ];

  return (
    <svg
      viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={`Proxidize ${config.asset} preview`}
      className={className}
    >
      {background && (
        <rect
          x={viewBox.x}
          y={viewBox.y}
          width={viewBox.w}
          height={viewBox.h}
          rx={background.radius}
          ry={background.radius}
          fill={background.fill}
        />
      )}
      {paths.map((p) => (
        <path
          key={p.d}
          d={p.d}
          fill={p.fill}
          stroke={p.stroke}
          strokeWidth={p.strokeWidth}
          strokeMiterlimit={p.strokeMiterlimit}
        />
      ))}
      {showGuides && config.padding && (
        <g aria-hidden="true">
          <rect
            x={content.x}
            y={content.y}
            width={content.w}
            height={content.h}
            fill="none"
            stroke={guideColor}
            strokeWidth={1}
            strokeDasharray="4 4"
            opacity={0.4}
            vectorEffect="non-scaling-stroke"
          />
          <rect
            x={content.x - o}
            y={content.y - o}
            width={content.w + 2 * o}
            height={content.h + 2 * o}
            fill="none"
            stroke={guideColor}
            strokeWidth={1}
            strokeDasharray="2 5"
            opacity={0.25}
            vectorEffect="non-scaling-stroke"
          />
          {corners.map((corner) => (
            <path
              key={`${corner.x}-${corner.y}`}
              d={O_GLYPH_PATH}
              fill={guideColor}
              opacity={0.22}
              transform={`translate(${corner.x + ghostInset - O_BOUNDS.x * ghostScale} ${
                corner.y - O_BOUNDS.y * ghostScale
              }) scale(${ghostScale})`}
            />
          ))}
        </g>
      )}
    </svg>
  );
}
